import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Calendar, PlusCircle, X, CheckCircle2, AlertTriangle, Unlock, ShieldCheck, Trash2, Edit3 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { getUserPermissions } from '../../utils/permissions_utils';
import API, { socket } from '../../services/api';

const AnneeScolaire = ({ user }) => {
    const navigate = useNavigate();
    const userPerms = useMemo(() => getUserPermissions(), []);
    
    const canManageAnnee = true; // Ajustable selon tes permissions

    const [annees, setAnnees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    
    // État pour savoir si on est en train de modifier une année existante
    const [editingId, setEditingId] = useState(null);
    
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [confirmAction, setConfirmAction] = useState({ show: false, id: null, type: null, payload: null, message: '' });
    
    const [formData, setFormData] = useState({
        libelle: `ANNEE SCOLAIRE ${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
        date_debut: `${new Date().getFullYear()}-09-01`,
        date_fin: `${new Date().getFullYear() + 1}-07-31`
    });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    const fetchAnnees = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get('/scolarite/annees/liste');
            if (res.data.success) {
                const liste = res.data.data;
                setAnnees(liste);
                
                const anneeOuverte = liste.find(a => a.statut === 'OUVERT') || liste[0];
                if (anneeOuverte) {
                    localStorage.setItem('currentAnneeScolaireId', anneeOuverte.id);
                }
            }
        } catch (err) {
            showToast("Erreur de chargement des années scolaires", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAnnees();
        if (socket && user?.company_id) {
            const room = String(user.company_id);
            socket.emit('join_company', room);
            socket.on('DATA_EVENT', (event) => {
                if (event.table === 'annees_scolaires') {
                    fetchAnnees();
                }
            });
        }
        return () => {
            if (socket) {
                socket.off('DATA_EVENT');
            }
        };
    }, [user?.company_id, socket, fetchAnnees]);

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({
            libelle: `ANNEE SCOLAIRE ${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
            date_debut: `${new Date().getFullYear()}-09-01`,
            date_fin: `${new Date().getFullYear() + 1}-07-31`
        });
    };

    const handleOpenCreate = () => {
        if (showForm && !editingId) {
            resetForm();
        } else {
            setEditingId(null);
            setFormData({
                libelle: `ANNEE SCOLAIRE ${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
                date_debut: `${new Date().getFullYear()}-09-01`,
                date_fin: `${new Date().getFullYear() + 1}-07-31`
            });
            setShowForm(true);
        }
    };

    const handleEditClick = (annee) => {
        setEditingId(annee.id);
        // Formatage des dates pour les inputs de type 'date' (YYYY-MM-DD) s'il y a des timestamps
        const formatDateInput = (dateStr) => dateStr ? dateStr.split('T')[0] : '';
        
        setFormData({
            libelle: annee.libelle,
            date_debut: formatDateInput(annee.date_debut),
            date_fin: formatDateInput(annee.date_fin)
        });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            if (editingId) {
                // Modification
                const res = await API.put(`/scolarite/annees/${editingId}`, formData);
                showToast(res.data.message || "Année scolaire mise à jour avec succès.");
            } else {
                // Création
                const res = await API.post('/scolarite/annees/creer', formData);
                showToast(res.data.message || "Nouvelle année scolaire ouverte avec succès.");
            }
            resetForm();
            fetchAnnees();
        } catch (err) {
            showToast(err.response?.data?.error || "Une erreur est survenue", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const askToggleStatus = (annee, nextStatut) => {
        const isCloture = nextStatut === 'CLOTURE_TOTALE';
        setConfirmAction({
            show: true,
            id: annee.id,
            type: 'STATUS',
            payload: nextStatut,
            message: isCloture 
                ? `Voulez-vous procéder à la CLÔTURE TOTALE de l'année ${annee.libelle} ? Cette action verrouillera définitivement les notes et paiements.`
                : `Voulez-vous réouvrir l'année ${annee.libelle} ?`
        });
    };

    const askDelete = (annee) => {
        setConfirmAction({
            show: true,
            id: annee.id,
            type: 'DELETE',
            payload: null,
            message: `Confirmer la suppression de l'année ${annee.libelle} ? (Bloqué si des classes ou des inscriptions y sont liées).`
        });
    };

    const processConfirm = async () => {
        const { id, type, payload } = confirmAction;
        setConfirmAction({ show: false });
        setActionLoading(true);
        try {
            if (type === 'STATUS') {
                await API.put(`/scolarite/annees/statut/${id}`, { statut: payload });
                showToast(`Statut de l'année mis à jour avec succès.`);
            } else if (type === 'DELETE') {
                const res = await API.delete(`/scolarite/annees/${id}`);
                showToast(res.data.message || "Année scolaire supprimée avec succès.");
            }
            fetchAnnees();
        } catch (err) {
            showToast(err.response?.data?.error || "Action refusée", "error");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div style={layoutStyle}>
            <Sidebar />
            <main style={mainStyle}>
                <header style={headerStyle}>
                    <div style={titleContainer}>
                        <div style={iconBoxStyle}><Calendar size={22} color="white" /></div>
                        <div>
                            <h1 style={titleStyle}>ANNÉES SCOLAIRES</h1>
                            <div style={subtitleStyle}><ShieldCheck size={14} /> Gestion du calendrier académique</div>
                        </div>
                    </div>
                    <button 
                        onClick={handleOpenCreate} 
                        style={showForm && !editingId ? btnCancel : btnAdd}
                    >
                        {showForm && !editingId ? <><X size={18} /> ANNULER</> : <><PlusCircle size={18} /> OUVRIR UNE ANNÉE</>}
                    </button>
                </header>

                {showForm && (
                    <div style={formWrapper}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                            <span style={{fontSize: '11px', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase'}}>
                                {editingId ? `Modifier l'année scolaire` : 'Ouverture d\'une nouvelle année'}
                            </span>
                            <button onClick={resetForm} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#64748b'}}>
                                <X size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} style={inlineForm}>
                            <div style={inputGroup}>
                                <label style={labelStyle}>DATE DÉBUT</label>
                                <input 
                                    type="date" 
                                    style={inputStyle} 
                                    required 
                                    value={formData.date_debut}
                                    onChange={(e) => setFormData({...formData, date_debut: e.target.value})}
                                />
                            </div>
                            <div style={inputGroup}>
                                <label style={labelStyle}>DATE FIN</label>
                                <input 
                                    type="date" 
                                    style={inputStyle} 
                                    required 
                                    value={formData.date_fin}
                                    onChange={(e) => setFormData({...formData, date_fin: e.target.value})}
                                />
                            </div>
                            <div style={inputGroup}>
                                <label style={labelStyle}>LIBELLÉ</label>
                                <input 
                                    style={inputStyle} 
                                    required
                                    value={formData.libelle} 
                                    onChange={(e) => setFormData({...formData, libelle: e.target.value})} 
                                />
                            </div>
                            <button type="submit" disabled={actionLoading} style={btnSubmit}>
                                {actionLoading ? <X className="animate-spin" /> : (editingId ? "METTRE À JOUR" : "VALIDER")}
                            </button>
                        </form>
                    </div>
                )}

                <div style={tableWrapper}>
                    {loading ? (
                        <div style={centerStyle}><Calendar className="animate-spin" size={40} color="#2563eb" /></div>
                    ) : (
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>ANNÉE SCOLAIRE</th>
                                    <th style={thStyle}>PÉRIODE</th>
                                    <th style={thStyle}>STATUT</th>
                                    <th style={thStyle}>ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {annees.map(annee => (
                                    <tr key={annee.id} style={trStyle}>
                                        <td style={tdStyle}><strong>{annee.libelle}</strong></td>
                                        <td style={tdStyle}>{annee.date_debut} ⮕ {annee.date_fin}</td>
                                        <td style={tdStyle}>
                                            <span style={badgeStyle(annee.statut)}>{annee.statut}</span>
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={{display:'flex', gap:'10px', alignItems: 'center'}}>
                                                {annee.statut === 'OUVERT' ? (
                                                    <button onClick={() => askToggleStatus(annee, 'CLOTURE_TOTALE')} style={btnStatusDef}>
                                                        CLÔTURER L'ANNÉE
                                                    </button>
                                                ) : (
                                                    <button onClick={() => askToggleStatus(annee, 'OUVERT')} style={btnStatusReopen}>
                                                        <Unlock size={14} /> RÉOUVRIR
                                                    </button>
                                                )}
                                                <button onClick={() => handleEditClick(annee)} style={btnEdit} title="Modifier l'année">
                                                    <Edit3 size={18} />
                                                </button>
                                                <button onClick={() => askDelete(annee)} style={btnDelete} title="Supprimer l'année scolaire">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {annees.length === 0 && (
                                    <tr>
                                        <td colSpan="4" style={{textAlign: 'center', padding: '30px', color: '#64748b'}}>Aucune année scolaire configurée.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {confirmAction.show && (
                    <div style={confirmToastStyle}>
                        <div style={{display:'flex', alignItems:'center', gap:'12px', flex: 1}}>
                            <div style={warnIcon}><AlertTriangle size={20} color="white"/></div>
                            <span style={confirmText}>{confirmAction.message}</span>
                        </div>
                        <div style={{display:'flex', gap:'10px'}}>
                            <button onClick={()=>setConfirmAction({show:false})} style={btnToastCancel}>ANNULER</button>
                            <button onClick={processConfirm} style={btnToastConfirm}>CONFIRMER</button>
                        </div>
                    </div>
                )}

                {toast.show && (
                    <div style={{...toastStyle, background: toast.type === 'success' ? '#0f172a' : '#dc2626'}}>
                        {toast.type === 'success' ? <CheckCircle2 size={18} color="#10b981" /> : <AlertTriangle size={18} color="white" />}
                        {toast.message}
                    </div>
                )}
            </main>
        </div>
    );
};

// --- STYLES ---
const layoutStyle = { display: 'flex', height: '100vh', background: '#f8fafc', overflow: 'hidden' };
const mainStyle = { flex: 1, display: 'flex', flexDirection: 'column' };
const headerStyle = { background: 'white', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const titleContainer = { display: 'flex', alignItems: 'center', gap: '15px' };
const titleStyle = { margin: 0, fontSize: '18px', fontWeight: '900', color: '#0f172a' };
const subtitleStyle = { fontSize: '12px', color: '#64748b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' };
const iconBoxStyle = { background: '#2563eb', padding: '10px', borderRadius: '12px' };
const tableWrapper = { padding: '20px 40px', flex: 1, overflowY: 'auto' };
const tableStyle = { width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px' };
const thStyle = { textAlign: 'left', padding: '10px', fontSize: '11px', color: '#64748b', fontWeight: '800', textTransform: 'uppercase' };
const trStyle = { background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' };
const tdStyle = { padding: '15px 10px', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', fontSize: '13px' };
const btnStatusDef = { background: '#0f172a', color: 'white', border: 'none', padding: '7px 15px', borderRadius: '8px', fontWeight: '800', fontSize: '11px', cursor: 'pointer' };
const btnStatusReopen = { background: '#ecfdf5', color: '#059669', border: '1px solid #d1fae5', padding: '6px 15px', borderRadius: '8px', fontWeight: '800', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' };
const btnDelete = { background: '#fef2f2', border: 'none', color: '#dc2626', padding: '8px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const btnEdit = { background: '#eff6ff', border: 'none', color: '#2563eb', padding: '8px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const badgeStyle = (s) => ({
    background: s === 'OUVERT' ? '#dcfce7' : '#f1f5f9',
    color: s === 'OUVERT' ? '#166534' : '#475569',
    padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '900'
});
const confirmToastStyle = { position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '15px 25px', borderRadius: '20px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', minWidth: '500px', zIndex: 9999, border: '2px solid #e2e8f0' };
const warnIcon = { background: '#f59e0b', padding: '8px', borderRadius: '12px' };
const confirmText = { fontWeight: '800', color: '#0f172a', fontSize: '13px' };
const btnToastConfirm = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' };
const btnToastCancel = { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' };
const btnAdd = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };
const btnCancel = { ...btnAdd, background: '#fee2e2', color: '#dc2626' };
const formWrapper = { background: 'white', padding: '15px 40px', borderBottom: '1px solid #e2e8f0' };
const inlineForm = { display: 'flex', alignItems: 'flex-end', gap: '20px' };
const inputGroup = { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#64748b' };
const inputStyle = { padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700', fontSize: '13px', width: '100%' };
const btnSubmit = { background: '#2563eb', color: 'white', border: 'none', padding: '11px 25px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer' };
const toastStyle = { position: 'fixed', top: '20px', right: '20px', padding: '15px 30px', borderRadius: '12px', color: 'white', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 9999, fontWeight: '700' };
const centerStyle = { display: 'flex', justifyContent: 'center', padding: '100px' };

export default AnneeScolaire;